// Normalises IoC and CVE alerts into one inbox item shape.

export const SEVS = ['crit', 'high', 'med', 'low'];
export const SEV_LABEL = { crit: 'Critical', high: 'High', med: 'Medium', low: 'Low', unk: 'Unrated' };
export const SEV_NAME = { crit: 'CRITICAL', high: 'HIGH', med: 'MEDIUM', low: 'LOW', unk: 'UNRATED' };
const RANK = { crit: 0, high: 1, med: 2, low: 3, unk: 4 };

export const sevFg = (k) => (k === 'unk' ? 'var(--ink-faint)' : `var(--sev-${k}-fg)`);
export const sevBg = (k) => (k === 'unk' ? 'var(--surf-raised)' : `var(--sev-${k}-bg)`);

export const KILL_CHAIN = [
  ['Reconnaissance', 'Recon'], ['Weaponization', 'Weapon'], ['Delivery', 'Deliver'], ['Exploitation', 'Exploit'],
  ['Installation', 'Install'], ['Command and Control', 'C2'], ['Actions on Objectives', 'Act'],
];

export const IOC_TYPE_LABEL = { ip: 'IP address', domain: 'Domain', hash: 'File hash', filename: 'Filename', url: 'URL' };

export const PRIVATE_IP = /^(127\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/;
export const isPrivateIoc = (a) => a.ioc_type === 'ip' && PRIVATE_IP.test(a.ioc_value || '');

// Alerts raised by an organisation's own private indicators carry this source.
export const isPrivateAlert = (a) => a.source === 'misp-private';
export const sourceLabel = (a) => (isPrivateAlert(a) ? 'Private' : (a.source || 'threat intel'));

const IOC_SEV = { high: 'high', medium: 'med', low: 'low' };
const CVE_SEV = { CRITICAL: 'crit', HIGH: 'high', MEDIUM: 'med', LOW: 'low' };

export const parseTs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const ago = (iso) => {
  const d = parseTs(iso);
  if (!d) return '';
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 1) return 'now';
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h`;
  return `${Math.floor(m / 1440)} d`;
};

export const stamp = (iso) => {
  const d = parseTs(iso);
  return d ? d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kuala_Lumpur' }) : '—';
};

const iocTitle = (a) => {
  const v = a.ioc_value;
  switch (a.ioc_type) {
    case 'ip': return a.kill_chain_stage === 'Command and Control' ? `Connection to known C2 server ${v}` : `Connection to flagged IP ${v}`;
    case 'domain': return `DNS lookup for flagged domain ${v}`;
    case 'hash': return 'File matches known malware hash';
    case 'url': return `Request to flagged URL ${v}`;
    case 'filename': return `File with flagged name ${v}`;
    default: return `Indicator matched: ${v}`;
  }
};

export const fromIoc = (a, hostOf) => ({
  key: `ioc-${a.id}`,
  kind: 'ioc',
  id: a.id,
  sev: IOC_SEV[a.threat_level] || 'unk',
  title: iocTitle(a),
  host: hostOf(a.server_id),
  meta: `${hostOf(a.server_id)} · ${(IOC_TYPE_LABEL[a.ioc_type] || a.ioc_type || 'indicator').toLowerCase()} · ${sourceLabel(a).toUpperCase()}`,
  search: `${a.ioc_value} ${hostOf(a.server_id)}`.toLowerCase(),
  detected_at: a.detected_at,
  resolved: !!a.resolved,
  cvss: null,
  fp: !a.resolved && isPrivateIoc(a),
  raw: a,
});

export const fromCve = (a, hostOf) => ({
  key: `cve-${a.id}`,
  kind: 'cve',
  id: a.id,
  sev: CVE_SEV[a.severity] || 'unk',
  title: `${a.cve_id} · ${a.package_name} ${a.version}`,
  host: hostOf(a.server_id),
  meta: `${hostOf(a.server_id)} · ${a.package_name} ${a.version}${a.cvss_score != null ? ` · CVSS ${Number(a.cvss_score).toFixed(1)}` : ''}`,
  search: `${a.cve_id} ${a.package_name} ${hostOf(a.server_id)}`.toLowerCase(),
  detected_at: a.detected_at,
  resolved: !!a.resolved,
  cvss: a.cvss_score,
  fp: false,
  raw: a,
});

const newest = (a, b) => (parseTs(b.detected_at)?.getTime() || 0) - (parseTs(a.detected_at)?.getTime() || 0);

export const SORTS = {
  severity: (a, b) => RANK[a.sev] - RANK[b.sev] || newest(a, b),
  newest,
  cvss: (a, b) => (b.cvss ?? -1) - (a.cvss ?? -1) || RANK[a.sev] - RANK[b.sev] || newest(a, b),
};
export const SORT_LABEL = { severity: 'Severity, then newest', newest: 'Newest first', cvss: 'Highest CVSS' };
